/*
==============================================================================
Código hecho por Rcrea
Archivo: assets/section-rcrea-product-carousel.js
Sección: Güten Brew | Product Carousel Tabs
Función:
- Tabs
- Portada multimedia
- Reproducción robusta video Shopify
- Dots
- Flechas
- Hover segunda imagen
- Add to Cart (patrón nativo del tema: cart-drawer.renderContents)
==============================================================================
*/
(() => {
  const ROOT_SELECTOR = "[data-rpc-product-carousel]";
  const ITEM_SELECTOR = "[data-rpc-item]";
  const VIDEO_SELECTOR = "[data-rpc-cover-video]";
  const INIT_ATTR = "data-rpc-initialized";
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const getRoots = (root = document) => {
    const results = [];
    if (root.matches?.(ROOT_SELECTOR)) results.push(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach((item) => results.push(item));
    return results;
  };
  const getActivePanel = (root) => root.querySelector(".rpc-panel.is-active");
  const getActiveTrack = (root) => getActivePanel(root)?.querySelector("[data-rpc-track]");
  const getItems = (track) => track ? Array.from(track.querySelectorAll(ITEM_SELECTOR)) : [];
  const getGap = (track) => {
    if (!track) return 0;
    const style = window.getComputedStyle(track);
    return Number.parseFloat(style.columnGap || style.gap || "0") || 0;
  };
  const getPerPage = (track) => {
    if (!track) return 1;
    const cssValue = window.getComputedStyle(track).getPropertyValue("--rpc-per-view").trim();
    const value = Number.parseInt(cssValue, 10);
    if (Number.isFinite(value) && value > 0) return value;
    const items = getItems(track);
    if (!items.length) return 1;
    const itemWidth = items[0].getBoundingClientRect().width;
    const gap = getGap(track);
    if (!itemWidth) return 1;
    return Math.max(1, Math.round((track.clientWidth + gap) / (itemWidth + gap)));
  };
  const getPageStarts = (track) => {
    const items = getItems(track);
    if (!track || !items.length) return [0];
    const perPage = getPerPage(track);
    const itemWidth = items[0].getBoundingClientRect().width;
    const gap = getGap(track);
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll <= 1 || itemWidth <= 0) return [0];
    const pageCount = Math.ceil(items.length / perPage);
    const pageStep = perPage * (itemWidth + gap);
    const starts = [0];
    for (let page = 1; page < pageCount; page += 1) {
      const position = Math.min(page * pageStep, maxScroll);
      const exists = starts.some((existing) => Math.abs(existing - position) < 2);
      if (!exists) starts.push(position);
    }
    if (starts.length > 1) starts[starts.length - 1] = maxScroll;
    return starts;
  };
  const findActivePage = (track, starts) => {
    if (!track || !starts.length) return 0;
    const current = Math.max(0, track.scrollLeft);
    let closestIndex = 0;
    let closestDistance = Infinity;
    starts.forEach((start, index) => {
      const distance = Math.abs(start - current);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  };
  const updateControls = (root) => {
    const track = getActiveTrack(root);
    const dots = root.querySelector("[data-rpc-dots]");
    const prev = root.querySelector("[data-rpc-prev]");
    const next = root.querySelector("[data-rpc-next]");
    if (!track || !dots) return;
    const items = getItems(track);
    const perPage = getPerPage(track);
    const starts = getPageStarts(track);
    const activePage = findActivePage(track, starts);
    const multiple = items.length > perPage && starts.length > 1;
    root.classList.toggle("has-multiple-pages", multiple);
    dots.innerHTML = "";
    if (multiple) {
      starts.forEach((start, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = `rpc-dot${index === activePage ? " is-active" : ""}`;
        dot.setAttribute("aria-label", `Ir al grupo ${index + 1}`);
        dot.addEventListener("click", () => {
          track.scrollTo({
            left: start,
            behavior: "smooth"
          });
        });
        dots.appendChild(dot);
      });
    }
    if (prev) prev.disabled = !multiple || activePage <= 0;
    if (next) next.disabled = !multiple || activePage >= starts.length - 1;
  };
  const movePage = (root, direction) => {
    const track = getActiveTrack(root);
    if (!track) return;
    const starts = getPageStarts(track);
    const activePage = findActivePage(track, starts);
    const targetIndex = clamp(activePage + direction, 0, starts.length - 1);
    track.scrollTo({
      left: targetIndex === 0 ? 0 : starts[targetIndex],
      behavior: "smooth"
    });
    window.setTimeout(() => {
      updateControls(root);
    }, 420);
  };
  /*
  ============================================================================
  HOVER SEGUNDA IMAGEN PRODUCTO
  ============================================================================
  */
  const clearMediaHover = (root, exception = null) => {
    root.querySelectorAll(".rpc-card.is-media-hovered").forEach((card) => {
      if (card !== exception) card.classList.remove("is-media-hovered");
    });
  };
  const updateProductImageHover = (event, root) => {
    if (event.pointerType && event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    const card = event.target.closest("[data-rpc-card]");
    if (!card || !root.contains(card)) {
      clearMediaHover(root);
      return;
    }
    clearMediaHover(root, card);
    if (!card.classList.contains("rpc-card--has-secondary")) return;
    const media = card.querySelector("[data-rpc-product-media]");
    if (!media) return;
    const rect = media.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    card.classList.toggle("is-media-hovered", inside);
  };
  /*
  ============================================================================
  VIDEOS PORTADA
  ============================================================================
  */
  const prepareVideo = (video) => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("loop", "");
    video.setAttribute("autoplay", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
  };
  const attemptPlay = (video) => {
    if (!video) return;
    prepareVideo(video);
    const executePlay = () => {
      const promise = video.play();
      if (promise && typeof promise.catch === "function") {
        promise.catch(() => {});
      }
    };
    if (video.readyState >= 2) {
      executePlay();
      return;
    }
    try {
      video.load();
    } catch (error) {}
    video.addEventListener("loadeddata", executePlay, { once: true });
    video.addEventListener("canplay", executePlay, { once: true });
  };
  const pauseVideo = (video) => {
    if (!video) return;
    try {
      video.pause();
    } catch (error) {}
  };
  const getActiveCoverVideo = (cover) => {
    if (!cover) return null;
    const hasMobileMedia = cover.classList.contains("rpc-cover--has-mobile-media");
    if (mobileQuery.matches && hasMobileMedia) {
      return cover.querySelector('.rpc-cover__media--mobile [data-rpc-cover-video]');
    }
    return cover.querySelector('.rpc-cover__media--desktop [data-rpc-cover-video]');
  };
  const syncCover = (cover) => {
    if (!cover) return;
    const panel = cover.closest("[data-rpc-panel]");
    const panelActive = panel && panel.classList.contains("is-active") && !panel.hidden;
    const activeVideo = getActiveCoverVideo(cover);
    cover.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (!panelActive || video !== activeVideo) {
        pauseVideo(video);
        return;
      }
      attemptPlay(video);
    });
  };
  const syncCoverVideos = (root) => {
    root.querySelectorAll("[data-rpc-cover]").forEach((cover) => {
      syncCover(cover);
    });
  };
  const initCoverVideoEvents = (root) => {
    root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      prepareVideo(video);
      const retry = () => {
        const cover = video.closest("[data-rpc-cover]");
        const panel = video.closest("[data-rpc-panel]");
        if (!cover || !panel || panel.hidden || !panel.classList.contains("is-active")) return;
        if (getActiveCoverVideo(cover) !== video) return;
        attemptPlay(video);
      };
      video.addEventListener("loadedmetadata", retry);
      video.addEventListener("loadeddata", retry);
      video.addEventListener("canplay", retry);
      video.addEventListener("stalled", () => {
        window.setTimeout(retry, 250);
      });
    });
  };
  /*
  ============================================================================
  TABS
  ============================================================================
  */
  const activateTab = (root, targetName) => {
    root.querySelectorAll("[data-rpc-tab]").forEach((tab) => {
      const active = tab.dataset.rpcTab === targetName;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
    root.querySelectorAll("[data-rpc-panel]").forEach((panel) => {
      const active = panel.dataset.rpcPanel === targetName;
      panel.classList.toggle("is-active", active);
      panel.hidden = !active;
      if (active) {
        const track = panel.querySelector("[data-rpc-track]");
        if (track) track.scrollLeft = 0;
      }
    });
    clearMediaHover(root);
    window.requestAnimationFrame(() => {
      syncCoverVideos(root);
      window.requestAnimationFrame(() => {
        updateControls(root);
        syncCoverVideos(root);
      });
    });
  };
  /*
  ============================================================================
  CARRITO — Patrón nativo del tema (Enterprise)
  ----------------------------------------------------------------------------
  Este tema abre y refresca el drawer con un único método nativo:

      cartDrawer.renderContents(data, true)   // 2do arg = abrir el drawer

  donde `data` es la respuesta de cart/add.js pedida CON `sections` en el body.
  renderContents reemplaza el HTML del drawer Y lo abre, gestionando overlay,
  aria-hidden, scroll-lock y foco de forma coherente. Es exactamente lo que
  hace product-form.js del tema (el add-to-cart que funciona bien).

  NO forzamos `open`, NO activamos overlays a mano, NO reemplazamos el nodo
  <cart-drawer>. Eso era lo que dejaba la capa de opacidad encima del drawer.
  ============================================================================
  */
  const getThemeRoutes = () => window.theme?.routes || {};

  const getCartAddUrl = () => {
    return getThemeRoutes().cartAdd || window.routes?.cart_add_url || "/cart/add";
  };

  const getCartUrl = () => {
    const cart = getThemeRoutes().cart || window.routes?.cart_url || "/cart";
    return cart.replace(/\/+$/, "");
  };

  const getCartDrawer = () => document.querySelector("cart-drawer");

  const emitCartAdd = (variantId, response, cart) => {
    document.dispatchEvent(
      new CustomEvent("rcrea:cart:add", {
        bubbles: true,
        detail: { variantId, response, cart }
      })
    );
  };

  const emitCartError = (message) => {
    document.dispatchEvent(
      new CustomEvent("rcrea:cart:error", {
        bubbles: true,
        detail: { error: message }
      })
    );
  };

  // Fallback: actualizar la burbuja del carrito si no hay renderContents.
  const updateCartCount = (cart) => {
    if (!cart || typeof cart.item_count !== "number") return;
    const count = String(cart.item_count);
    document
      .querySelectorAll(".cart-count-bubble span:first-child, [data-cart-count]")
      .forEach((element) => {
        element.textContent = count;
      });
  };

  const readCart = async () => {
    try {
      const response = await fetch(`${getCartUrl()}.js`, {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      return null;
    }
  };

  const buildFormData = (button) => {
    const form = button.closest("[data-rpc-card-form]") || button.closest("form");
    const variantId = String(
      button.dataset.variantId || form?.querySelector('[name="id"]')?.value || ""
    ).trim();

    if (!variantId) {
      return { form, variantId: "", formData: null };
    }

    const formData = form ? new FormData(form) : new FormData();
    formData.set("id", variantId);
    if (!formData.get("quantity")) {
      formData.set("quantity", "1");
    }

    return { form, variantId, formData };
  };

  // Asegura que las hojas de estilo del drawer (cart-items.css, etc.) que se
  // cargan con media="print" queden activas tras un re-render del drawer.
  const activateDrawerStylesheets = () => {
    const apply = () => {
      document
        .querySelectorAll('cart-drawer link[rel="stylesheet"][media="print"]')
        .forEach((link) => {
          link.media = "all";
        });
      // Por si alguna hoja del drawer está fuera del <cart-drawer> (cabecera).
      document
        .querySelectorAll('link[rel="stylesheet"][media="print"]')
        .forEach((link) => {
          const href = link.getAttribute("href") || "";
          if (/cart-items|promos|cart-drawer/i.test(href)) {
            link.media = "all";
          }
        });
    };
    apply();
    // Repetir en el siguiente frame por si el HTML se inserta de forma diferida.
    window.requestAnimationFrame(apply);
  };

  const addToCart = async (root, button) => {
    if (!button || button.disabled || button.classList.contains("is-loading")) return;

    const { form, variantId, formData } = buildFormData(button);

    if (!variantId || !formData) {
      console.warn("[Rcrea Product Carousel] Falta ID de variante en el botón:", button);
      emitCartError("Falta el ID de variante del producto.");
      button.classList.add("is-error");
      window.setTimeout(() => button.classList.remove("is-error"), 900);
      return;
    }

    const cartDrawer = getCartDrawer();

    // Pedir las secciones que el tema necesita para refrescar el drawer,
    // igual que product-form.js del tema.
    let sections = "cart-icon-bubble";
    if (cartDrawer) {
      const drawerSection = cartDrawer.closest(".shopify-section");
      if (drawerSection?.id) {
        sections += `,${drawerSection.id.replace("shopify-section-", "")}`;
      }
    }
    formData.append("sections", sections);
    formData.append("sections_url", window.location.pathname);

    button.classList.remove("is-error", "is-added");
    button.classList.add("is-loading");
    button.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(getCartAddUrl(), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        throw new Error(
          data?.description || data?.message || "No fue posible agregar el producto."
        );
      }

// Camino principal: usar el propio componente del tema.
      // renderContents(data, true) refresca el HTML del drawer Y lo abre,
      // manejando overlay / aria-hidden / scroll-lock de forma coherente.
      if (cartDrawer && typeof cartDrawer.renderContents === "function") {
        cartDrawer.renderContents(data, true);
        // El drawer carga cart-items.css con media="print" y lo activa en su
        // onload. Al re-renderizar el HTML, ese <link> puede no re-disparar
        // onload y quedar sin aplicar (imagen gigante / layout crudo).
        // Forzamos que cualquier hoja del drawer en media="print" pase a "all".
        activateDrawerStylesheets();
        emitCartAdd(variantId, data, null);
      } else {
        // Fallback muy improbable: sin cart-drawer disponible.
        const cart = await readCart();
        updateCartCount(cart);
        emitCartAdd(variantId, data, cart);
        // Actualizar la burbuja del ícono si vino en las secciones.
        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble && data?.sections?.["cart-icon-bubble"]) {
          bubble.innerHTML = data.sections["cart-icon-bubble"];
        }
      }

      button.classList.add("is-added");
      window.setTimeout(() => button.classList.remove("is-added"), 900);
    } catch (error) {
      console.error("[Rcrea Product Carousel] Add to cart falló", error);
      emitCartError(error?.message || "No fue posible agregar el producto.");
      button.classList.add("is-error");
      window.setTimeout(() => button.classList.remove("is-error"), 900);
    } finally {
      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");
    }
  };
  /*
  ============================================================================
  INICIALIZACIÓN
  ============================================================================
  */
  const initRoot = (root) => {
    if (!root || root.hasAttribute(INIT_ATTR)) return;
    root.setAttribute(INIT_ATTR, "true");
    let resizeTimer = null;
    let scrollFrame = null;
    const tabHandlers = [];
    const trackHandlers = [];
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        updateControls(root);
        syncCoverVideos(root);
      }, 120);
    };
    const onTrackScroll = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        updateControls(root);
        scrollFrame = null;
      });
    };
    const onRootClick = (event) => {
      const button = event.target.closest("[data-rpc-add-to-cart]");
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      addToCart(root, button);
    };

    const onRootSubmit = (event) => {
      const form = event.target.closest?.("[data-rpc-card-form]");
      if (!form || !root.contains(form)) return;
      event.preventDefault();
      event.stopPropagation();
      const button = form.querySelector("[data-rpc-add-to-cart]");
      addToCart(root, button);
    };
    const onPointerMove = (event) => {
      updateProductImageHover(event, root);
    };
    const onPointerLeave = () => {
      clearMediaHover(root);
    };
    root.querySelectorAll("[data-rpc-tab]").forEach((tab) => {
      const handler = () => {
        activateTab(root, tab.dataset.rpcTab);
      };
      tab.addEventListener("click", handler);
      tabHandlers.push({
        tab,
        handler
      });
    });
    root.querySelectorAll("[data-rpc-track]").forEach((track) => {
      track.addEventListener("scroll", onTrackScroll, {
        passive: true
      });
      trackHandlers.push(track);
    });
    const prev = root.querySelector("[data-rpc-prev]");
    const next = root.querySelector("[data-rpc-next]");
    const onPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      movePage(root, -1);
    };
    const onNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      movePage(root, 1);
    };
    prev?.addEventListener("click", onPrev);
    next?.addEventListener("click", onNext);
    root.addEventListener("click", onRootClick);
    root.addEventListener("submit", onRootSubmit);
    root.addEventListener("pointermove", onPointerMove, {
      passive: true
    });
    root.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", onResize, {
      passive: true
    });
    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", onResize);
    } else if (typeof mobileQuery.addListener === "function") {
      mobileQuery.addListener(onResize);
    }
    initCoverVideoEvents(root);
    window.requestAnimationFrame(() => {
      updateControls(root);
      syncCoverVideos(root);
      window.setTimeout(() => {
        syncCoverVideos(root);
      }, 250);
      window.setTimeout(() => {
        syncCoverVideos(root);
      }, 800);
    });
    root._rpcCleanup = () => {
      window.clearTimeout(resizeTimer);
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      window.removeEventListener("resize", onResize);
      root.removeEventListener("click", onRootClick);
      root.removeEventListener("submit", onRootSubmit);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerleave", onPointerLeave);
      prev?.removeEventListener("click", onPrev);
      next?.removeEventListener("click", onNext);
      tabHandlers.forEach(({ tab, handler }) => {
        tab.removeEventListener("click", handler);
      });
      trackHandlers.forEach((track) => {
        track.removeEventListener("scroll", onTrackScroll);
      });
      if (typeof mobileQuery.removeEventListener === "function") {
        mobileQuery.removeEventListener("change", onResize);
      } else if (typeof mobileQuery.removeListener === "function") {
        mobileQuery.removeListener(onResize);
      }
      root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
        pauseVideo(video);
      });
    };
  };
  const destroyRoot = (root) => {
    if (!root) return;
    if (typeof root._rpcCleanup === "function") {
      root._rpcCleanup();
    }
    root.removeAttribute(INIT_ATTR);
    delete root._rpcCleanup;
  };
  const initAll = (root = document) => {
    getRoots(root).forEach(initRoot);
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll();
    });
  } else {
    initAll();
  }
  /*
  ============================================================================
  SHOPIFY EDITOR
  ============================================================================
  */
  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    getRoots(event.target).forEach(destroyRoot);
  });
  document.addEventListener("shopify:block:select", (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!root) return;
    const item = event.target.closest?.("[data-rpc-block-id]");
    if (!item) return;
    const targetTab = item.dataset.rpcBlockTab;
    if (targetTab) {
      activateTab(root, targetTab);
    }
    window.setTimeout(() => {
      item.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
      syncCoverVideos(root);
    }, 80);
  });
})();